<template>
  <div class="bg-theme-nav p-1 sticky top-0 z-50 shadow-sm">
    <div class="w-12/12 md:w-6/6 lg:w-6/6 xl:w-6/6 mx-auto px-2">

      <header class="text-theme-nav-text flex justify-between items-center">
        <!-- Logo (switches based on theme) -->
        <RouterLink :to="{ name: 'home' }" class="flex items-center focus:outline-none rounded" aria-label="Home">
          <img
            :src="isDark ? darkLogo : lightLogo"
            alt="Company Logo"
            class="h-16 w-auto"
            loading="eager"
            width="64"
            height="64"
          >
        </RouterLink>

        <!-- Role Switch (Authenticated Users) -->
        <div
          v-if="authStore.isAuthenticated"
          class="hidden md:flex items-center space-x-3"
          aria-label="Role switcher"
        >
          <div class="flex flex-col items-start">
            <div class="flex rounded-full bg-theme-bg2 border border-theme-border overflow-hidden px-1.5 py-1">
              <!-- Rent chip -->
              <button
                type="button"
                @click="requestRoleSwitch('lessee')"
                :class="[
                  'flex items-center px-4 py-1.5 text-sm font-medium transition-all duration-200 focus:outline-none rounded-full',
                  authStore.user?.role === 'lessee'
                    ? 'bg-secondary-500 shadow-md'
                    : 'text-theme-text2 hover:text-theme-text'
                ]"
              >
                <i class="pi pi-shopping-cart mr-1 text-base" aria-hidden="true"></i>
                <span>Rent</span>
              </button>

              <!-- List chip -->
              <button
                type="button"
                @click="requestRoleSwitch('lessor')"
                :class="[
                  'flex items-center px-4 py-1.5 text-sm font-medium transition-all duration-200 focus:outline-none rounded-full',
                  authStore.user?.role === 'lessor'
                    ? 'bg-secondary-500 shadow-md'
                    : 'text-theme-text2 hover:text-theme-text'
                ]"
              >
                <i class="pi pi-briefcase mr-1 text-base" aria-hidden="true"></i>
                <span>List</span>
              </button>
            </div>

            <span class="text-[11px] text-theme-muted mt-0.5 ">
              Tap <strong>Rent</strong> or <strong>List</strong> to switch mode.
            </span>
          </div>
        </div>

        <!-- Authenticated User Menu -->
        <div v-if="authStore.isAuthenticated" class="flex items-center space-x-3">
          <!-- Currency Picker -->
          <div class="relative hidden md:block">
            <button
              @click="showCurrencyMenu = !showCurrencyMenu"
              class="flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-theme-text2 hover:text-theme-text bg-theme-bg2 border border-theme-border rounded-lg transition-colors focus:outline-none focus:ring-1 focus:ring-primary-500/40"
              aria-label="Select currency"
            >
              <span class="font-semibold">{{ currency.symbol }}</span>
              <span class="text-xs">{{ currency.code }}</span>
              <i class="pi pi-chevron-down text-[10px] ml-0.5" aria-hidden="true"></i>
            </button>
            <div
              v-if="showCurrencyMenu"
              class="absolute right-0 top-full mt-1 w-44 bg-theme-card border border-theme-border rounded-lg shadow-theme-modal z-50 py-1 overflow-hidden"
            >
              <button
                v-for="c in currencies"
                :key="c.code"
                @click="setCurrency(c.code); showCurrencyMenu = false"
                :class="[
                  'w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors',
                  c.code === currency.code
                    ? 'bg-primary-500/10 text-primary-600 font-semibold'
                    : 'text-theme-text2 hover:bg-theme-bg2'
                ]"
              >
                <span class="w-6 font-bold">{{ c.symbol }}</span>
                <span>{{ c.name }}</span>
              </button>
            </div>
          </div>

          <!-- Browse Items Button for Lessee -->
          <RouterLink
            :to="{ name: 'categories' }"
            class="focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 focus:ring-offset-theme-nav rounded-lg"
          >
            <button
              class="px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-500 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95"
              aria-label="Browse items"
            >
              Browse Items
            </button>
          </RouterLink>

          <!-- Lease Out Button for Lessor -->
          <RouterLink
            v-if="authStore.user.role === 'lessor'"
            :to="{ name: 'list-item' }"
            class="hidden md:block focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 focus:ring-offset-theme-nav rounded-lg"
          >
            <button
              class="px-4 py-2 bg-secondary-500 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95"
              aria-label="Lease out item"
            >
              Lease Out
            </button>
          </RouterLink>

          <!-- Cart Icon for Lessee -->
          <div v-if="authStore.user.role === 'lessee'">
            <RouterLink
              to="/cart"
              class="relative inline-flex items-center focus:outline-none"
              aria-label="Shopping cart"
            >
              <i class="pi pi-shopping-cart text-3xl mx-2" aria-hidden="true"></i>
              <span
                v-if="cartStore.cart.length > 0"
                class="absolute -top-1.5 -right-0.5 min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-[11px] font-bold rounded-full px-1 shadow-sm"
                aria-hidden="true"
              >
                {{ cartStore.cart.length }}
              </span>
            </RouterLink>
          </div>

          <!-- Profile Link (direct) -->
          <RouterLink
            :to="{ name: 'Account' }"
            class="hidden lg:flex items-center focus:outline-none focus:ring-2 focus:ring-secondary-500 rounded-full"
            aria-label="Profile"
          >
            <img
              v-if="authStore.user?.image"
              :src="authStore.user.image"
              :alt="userInitials"
              class="w-10 h-10 rounded-full border-2 border-theme-border object-cover user-avatar"
              width="40"
              height="40"
              loading="eager"
            />
            <div
              v-else
              class="w-10 h-10 rounded-full border-2 border-theme-border bg-primary-600 text-white flex items-center justify-center text-sm font-bold select-none"
            >{{ userInitials }}</div>
          </RouterLink>
        </div>

        <!-- Not Authenticated User Menu -->
        <div v-else class="flex items-center space-x-3">
          <!-- Currency Picker (unauth) -->
          <div class="relative hidden md:block">
            <button
              @click="showCurrencyMenu = !showCurrencyMenu"
              class="flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-theme-text2 hover:text-theme-text bg-theme-bg2 border border-theme-border rounded-lg transition-colors focus:outline-none focus:ring-1 focus:ring-primary-500/40"
              aria-label="Select currency"
            >
              <span class="font-semibold">{{ currency.symbol }}</span>
              <span class="text-xs">{{ currency.code }}</span>
              <i class="pi pi-chevron-down text-[10px] ml-0.5" aria-hidden="true"></i>
            </button>
            <div
              v-if="showCurrencyMenu"
              class="absolute right-0 top-full mt-1 w-44 bg-theme-card border border-theme-border rounded-lg shadow-theme-modal z-50 py-1 overflow-hidden"
            >
              <button
                v-for="c in currencies"
                :key="c.code"
                @click="setCurrency(c.code); showCurrencyMenu = false"
                :class="[
                  'w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors',
                  c.code === currency.code
                    ? 'bg-primary-500/10 text-primary-600 font-semibold'
                    : 'text-theme-text2 hover:bg-theme-bg2'
                ]"
              >
                <span class="w-6 font-bold">{{ c.symbol }}</span>
                <span>{{ c.name }}</span>
              </button>
            </div>
          </div>
          <!-- Browse Items Button -->
          <RouterLink
            :to="{ name: 'categories' }"
            class="hidden lg:block focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 focus:ring-offset-theme-nav rounded-lg"
          >
            <button
              class="px-6 py-2 bg-gradient-to-r from-primary-600 to-secondary-500 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95"
              aria-label="Browse items"
            >
              Browse Items
            </button>
          </RouterLink>

          <!-- Login Button -->
          <RouterLink
            to="/login"
            class="hidden md:block focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 focus:ring-offset-theme-nav rounded-lg"
          >
            <button
              class="px-6 py-2 bg-primary-600 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95 hover:bg-primary-700"
              aria-label="Login"
            >
              Login
            </button>
          </RouterLink>

          <!-- Lease Out Button -->
          <RouterLink
            :to="{ name: 'list-item' }"
            class="focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 focus:ring-offset-theme-nav rounded-lg"
          >
            <button
              class="px-6 py-2 bg-secondary-500 text-white font-semibold rounded-lg shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl active:scale-95"
              aria-label="Lease out equipment"
            >
              Lease Out
            </button>
          </RouterLink>

          <!-- Cart Icon -->
          <div class="pr-4">
            <RouterLink
              to="/cart"
              class="relative inline-flex items-center focus:outline-none"
              aria-label="Shopping cart"
            >
              <i class="pi pi-shopping-cart text-3xl mx-2" aria-hidden="true"></i>
              <span
                v-if="cartStore.cart.length > 0"
                class="absolute -top-1.5 -right-0.5 min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-[11px] font-bold rounded-full px-1 shadow-sm"
                aria-hidden="true"
              >
                {{ cartStore.cart.length }}
              </span>
            </RouterLink>
          </div>
        </div>
      </header>
    </div>
  </div>

  <!-- Mobile Bottom Navigation -->
  <nav
    class="fixed bottom-0 left-0 right-0 bg-theme-nav/95 backdrop-blur-lg border-t border-theme-border md:hidden z-50"
    aria-label="Mobile navigation"
  >
    <div class="flex items-stretch justify-around px-1">
      <!-- Home -->
      <RouterLink
        to="/"
        class="mobile-nav-item group"
        :class="{ 'mobile-nav-active': $route.path === '/' }"
        aria-label="Home"
      >
        <div class="mobile-nav-icon-wrap">
          <i class="pi pi-home text-lg" aria-hidden="true"></i>
        </div>
        <span class="mobile-nav-label">Home</span>
      </RouterLink>

      <!-- Browse (lessee) / List (lessor) / Categories (unauth) -->
      <RouterLink
        v-if="authStore.isAuthenticated && authStore.user?.role === 'lessor'"
        to="/list-item"
        class="mobile-nav-item group"
        :class="{ 'mobile-nav-active': $route.path === '/list-item' }"
        aria-label="List item"
      >
        <div class="mobile-nav-icon-wrap">
          <i class="pi pi-plus-circle text-lg" aria-hidden="true"></i>
        </div>
        <span class="mobile-nav-label">List</span>
      </RouterLink>

      <RouterLink
        v-else
        to="/categories"
        class="mobile-nav-item group"
        :class="{ 'mobile-nav-active': $route.path.startsWith('/categories') }"
        aria-label="Browse"
      >
        <div class="mobile-nav-icon-wrap">
          <i class="pi pi-search text-lg" aria-hidden="true"></i>
        </div>
        <span class="mobile-nav-label">Browse</span>
      </RouterLink>

      <!-- Cart -->
      <RouterLink
        to="/cart"
        class="mobile-nav-item group"
        :class="{ 'mobile-nav-active': $route.path === '/cart' }"
        aria-label="Cart"
      >
        <div class="mobile-nav-icon-wrap relative">
          <i class="pi pi-shopping-cart text-lg" aria-hidden="true"></i>
          <span
            v-if="cartStore.cart.length > 0"
            class="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 shadow-sm"
          >
            {{ cartStore.cart.length > 9 ? '9+' : cartStore.cart.length }}
          </span>
        </div>
        <span class="mobile-nav-label">Cart</span>
      </RouterLink>

      <!-- Messages -->
      <RouterLink
        to="/new-profile/chats"
        class="mobile-nav-item group"
        :class="{ 'mobile-nav-active': $route.path === '/new-profile/chats' }"
        aria-label="Messages"
      >
        <div class="mobile-nav-icon-wrap relative">
          <i class="pi pi-comments text-lg" aria-hidden="true"></i>
          <span
            v-if="chatStore.totalUnread > 0"
            class="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 shadow-sm"
          >
            {{ chatStore.totalUnread > 9 ? '9+' : chatStore.totalUnread }}
          </span>
        </div>
        <span class="mobile-nav-label">Chats</span>
      </RouterLink>

      <!-- Profile -->
      <RouterLink
        to="/new-profile"
        class="mobile-nav-item group"
        :class="{ 'mobile-nav-active': $route.path === '/new-profile' || $route.path === '/new-profile/account' }"
        aria-label="Profile"
      >
        <div class="mobile-nav-icon-wrap">
          <template v-if="authStore.isAuthenticated && authStore.user?.image">
            <img
              :src="authStore.user.image"
              :alt="authStore.user.first_name || 'User'"
              class="w-6 h-6 rounded-full object-cover border border-theme-border group-[.mobile-nav-active]:border-primary-500"
            />
          </template>
          <div
            v-else-if="authStore.isAuthenticated"
            class="w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center text-[10px] font-bold select-none border border-theme-border group-[.mobile-nav-active]:border-primary-500"
          >{{ userInitials }}</div>
          <i v-else class="pi pi-user text-lg" aria-hidden="true"></i>
        </div>
        <span class="mobile-nav-label">Profile</span>
      </RouterLink>
    </div>
  </nav>

  <!-- Role Switch Confirmation Popup -->
  <div
    v-if="showRoleConfirm"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60"
  >
    <div class="bg-theme-card border border-theme-border rounded-xl p-5 max-w-sm w-full mx-4 shadow-xl">
      <h2 class="text-lg font-semibold text-theme-text mb-2">
        Switch to {{ pendingRole === 'lessee' ? 'Rent' : 'List' }} mode?
      </h2>
      <p class="text-sm text-theme-text2 mb-4">
        You'll now
        {{
          pendingRole === 'lessee'
            ? 'browse and rent equipment from other users.'
            : 'list your equipment for others to rent.'
        }}
      </p>
      <div class="flex justify-end space-x-3">
        <button
          type="button"
          @click="cancelRoleSwitch"
          class="px-4 py-2 text-sm rounded-lg border border-theme-border text-theme-text2 hover:bg-theme-bg2 focus:outline-none"
        >
          Cancel
        </button>
        <button
          type="button"
          @click="confirmRoleSwitch"
          class="px-4 py-2 text-sm rounded-lg bg-secondary-500 text-theme-text font-semibold hover:brightness-110 focus:outline-none"
        >
          OK
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onBeforeUnmount, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useTheme } from '@/composables/useTheme.js';
import darkLogo from '@/assets/images/dark_logo.png';
import lightLogo from '@/assets/images/logo.jpeg';
import { useCartStore } from '@/store/cart';
import { useEquipmentsStore } from '@/store/equipments';
import { useCompanyInfoStore } from '@/store/company';
import useNotifications from '@/store/notification';
import { useCurrency } from '@/composables/useCurrency';
import { useChatStore } from '@/store/chat';
import { useLogger } from '@/composables/useLogger';

const logger = useLogger('Navbar');

// Theme
const { isDark } = useTheme();

// Currency
const { currencies, currency, setCurrency } = useCurrency();
const showCurrencyMenu = ref(false);

// Constants
const MAX_BADGE_COUNT = 9;

// Stores
const authStore = useAuthStore();
const cartStore = useCartStore();
const store = useEquipmentsStore();
const company = useCompanyInfoStore();
const { showNotification } = useNotifications();
const chatStore = useChatStore();
const router = useRouter();

// State
// New state for role switch confirmation
const showRoleConfirm = ref(false);
const pendingRole = ref(null);

// Methods
const handleLogout = async () => {
  try {
    await authStore.logout();
  } catch (error) {
    logger.error('Logout failed', error);
    showNotification({
      title: 'Logout Error',
      message: 'There was an error logging out. Please try again.',
      type: 'error'
    });
  }
};

// Request a role switch via popup
const requestRoleSwitch = (targetRole) => {
  if (!authStore.user || authStore.user.role === targetRole) return;
  pendingRole.value = targetRole;
  showRoleConfirm.value = true;
};

// Confirm role switch after user clicks OK
const confirmRoleSwitch = () => {
  if (!pendingRole.value) {
    showRoleConfirm.value = false;
    return;
  }

  try {
    // Keep existing store logic: mimic a toggle then call updateUserRole
    authStore.isOn = !authStore.isOn;
    authStore.updateUserRole();
  } catch (error) {
    logger.error('Role switch failed', error);
    showNotification({
      title: 'Role Switch Error',
      message: 'Unable to switch role. Please try again.',
      type: 'error'
    });
  } finally {
    pendingRole.value = null;
    showRoleConfirm.value = false;
  }
};

// Cancel role switch
const cancelRoleSwitch = () => {
  pendingRole.value = null;
  showRoleConfirm.value = false;
};

// User initials fallback for avatar
const userInitials = computed(() => {
  const u = authStore.user;
  if (!u) return '?';
  if (u.first_name && u.last_name) return (u.first_name[0] + u.last_name[0]).toUpperCase();
  if (u.first_name) return u.first_name.substring(0, 2).toUpperCase();
  if (u.username) return u.username.substring(0, 2).toUpperCase();
  if (u.email) return u.email.substring(0, 2).toUpperCase();
  return '?';
});

// Chat polling is handled globally in App.vue
// (no polling code needed here)

// Lifecycle Hooks
onMounted(async () => {
  if (authStore.isAuthenticated) {
    try {
      await Promise.all([
        authStore.getUserData(),
        store.fetchUserEquipments()
      ]);
    } catch (error) {
      logger.error('Failed to load user data', error);
    }
  }

  try {
    await company.fetchCompanyInfo();
  } catch (error) {
    logger.error('Failed to load company info', error);
  }

  // Close currency dropdown on outside click
  document.addEventListener('click', closeCurrencyMenu);
});

const closeCurrencyMenu = (e) => {
  if (showCurrencyMenu.value && !e.target.closest('[aria-label="Select currency"]') && !e.target.closest('.currency-dropdown')) {
    showCurrencyMenu.value = false;
  }
};
onBeforeUnmount(() => {
  document.removeEventListener('click', closeCurrencyMenu);
});
</script>

<style scoped>
.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  background: var(--color-neutral-300, #ddd);
  /* Light gray background */
  color: var(--color-text, #1c1c1c);
}

/* Smooth transitions for interactive elements */
.router-link-active {
  color: var(--color-secondary-500, #ffc107);
}

/* Badge animation */
span[aria-hidden="true"] {
  @apply transition-transform duration-200;
}

/* Mobile nav link active state */
.router-link-active i,
.router-link-active small {
  color: var(--color-secondary-500, #ffc107);
}

/* ── Mobile bottom nav ── */
.mobile-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 2px;
  padding: 8px 2px 6px;
  color: var(--color-navText, #333);
  opacity: 0.7;
  transition: color 0.15s ease, opacity 0.15s ease;
  position: relative;
  text-decoration: none;
}
.mobile-nav-item:hover,
.mobile-nav-item:focus-visible {
  opacity: 0.9;
}
.mobile-nav-active {
  opacity: 1;
  color: var(--color-primary-500, #6366f1);
}
.mobile-nav-active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 25%;
  right: 25%;
  height: 2.5px;
  border-radius: 0 0 3px 3px;
  background: var(--color-primary-500, #6366f1);
}
.mobile-nav-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}
.mobile-nav-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1;
}
</style>
